import React, { useState } from 'react';
import { X, LogIn, UserPlus } from 'lucide-react';
import * as pocketbaseService from '../utils/pocketbaseService';

interface AuthModalProps {
  onClose: () => void;
  onSuccess: () => void;
}

export function AuthModal({ onClose, onSuccess }: AuthModalProps) {
  const [mode, setMode] = useState<'login' | 'signup'>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      if (mode === 'signup') {
        // Sign up
        console.log('Attempting signup...');
        const signupResponse = await pocketbaseService.signup(email, password, name);

        if (signupResponse.error) {
          throw new Error(signupResponse.error);
        }

        // After signup, show success message
        setError('Compte créé avec succès ! Connexion en cours...');
        // Wait a moment for the message to be visible
        await new Promise(resolve => setTimeout(resolve, 500));
      }

      // Login (either after signup or direct login)
      console.log('Attempting login...');
      const loginResponse = await pocketbaseService.login(email, password);

      if (loginResponse.error) {
        // Translate common error messages to French
        let errorMessage = loginResponse.error;
        if (errorMessage.includes('credentials')) {
          errorMessage = 'Email ou mot de passe incorrect';
        }
        throw new Error(errorMessage);
      }

      console.log('Login successful');
      onSuccess();
    } catch (err: any) {
      console.error('Authentication error:', err);
      setError(err.message || 'Une erreur s\'est produite');
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6 relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="mb-6">
          <h2 className="text-2xl mb-2">
            {mode === 'login' ? 'Connexion' : 'Inscription'}
          </h2>
          <p className="text-sm text-gray-600">
            {mode === 'login' 
              ? 'Connectez-vous pour accéder à vos plans dans le cloud'
              : 'Créez un compte pour partager vos plans'}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {mode === 'signup' && (
            <div>
              <label className="block text-sm mb-1">Nom complet</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Jean Dupont"
              />
            </div>
          )}

          <div>
            <label className="block text-sm mb-1">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="vous@exemple.com"
            />
          </div>

          <div>
            <label className="block text-sm mb-1">Mot de passe</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={6}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="••••••••"
            />
            {mode === 'signup' && (
              <p className="text-xs text-gray-500 mt-1">
                Minimum 6 caractères
              </p>
            )}
          </div>

          {error && (
            <div className={`text-sm p-3 rounded-lg ${
              error.includes('créé') ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
            }`}>
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={isLoading}
            className="w-full bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {isLoading ? (
              'Chargement...'
            ) : mode === 'login' ? (
              <>
                <LogIn className="w-4 h-4" />
                Se connecter
              </>
            ) : (
              <>
                <UserPlus className="w-4 h-4" />
                Créer un compte
              </>
            )}
          </button>
        </form>

        <div className="mt-4 text-center">
          <button
            type="button"
            onClick={() => {
              setMode(mode === 'login' ? 'signup' : 'login');
              setError('');
            }}
            className="text-sm text-blue-600 hover:underline"
          >
            {mode === 'login' 
              ? 'Pas encore de compte ? Inscrivez-vous'
              : 'Déjà un compte ? Connectez-vous'}
          </button>
        </div>
      </div>
    </div>
  );
}